import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CollapsibleSection from './CollapsibleSection';

// Icons
const CalendarIcon = () => (
  <svg className="section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

export interface ShipmentWithoutBooking {
  id: string;
  reference: string;
  contractRef?: string;
  supplier: string;
  supplierCode?: string;
  buyer: string;
  buyerCode?: string;
  buyerName?: string;
  pol?: string;
  pod?: string;
  start?: string;
  end?: string;
  shipmentDisplay?: string;
  containerCount?: number;
  etd?: string;
  eta?: string;
  shipDate?: string; // legacy
  daysUntilEtd?: number | null;
  daysUntilShip?: number; // legacy
  daysUntilStart?: number | null;
  rangeStart?: string | null;
  rangeEnd?: string | null;
  // Category can be backend naming (past, thirty, sixty, ninety) or frontend naming (overdue, 30days, etc.)
  period?: string;
  category?: string;
  isOverdue?: boolean;
}

interface FilterTab {
  key: string;
  label: string;
  count: number;
}

interface ShipmentsWithoutBookingsSectionProps {
  shipments: ShipmentWithoutBooking[];
  onSendBR?: (shipment: ShipmentWithoutBooking) => void;
  onSearchGmail?: (shipment: ShipmentWithoutBooking) => void;
}

const ShipmentsWithoutBookingsSection: React.FC<ShipmentsWithoutBookingsSectionProps> = ({
  shipments,
  onSendBR,
  onSearchGmail,
}) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<string>('30days');

  // Helper to get category (supports both period and category)
  // Normalizes backend values ('past', 'thirty', etc.) to display values ('overdue', '30days', etc.)
  const getRawCategory = (s: ShipmentWithoutBooking) => s.period || s.category;
  const normalizeCategory = (cat: string | undefined): string => {
    if (!cat) return 'all';
    // Map backend naming to frontend display naming
    const categoryMap: Record<string, string> = {
      'past': 'overdue',
      'thirty': '30days',
      'sixty': '60days',
      'ninety': '90days',
      'discretionary': 'discretionary',
      // Also support direct frontend naming
      'overdue': 'overdue',
      '30days': '30days',
      '60days': '60days',
      '90days': '90days',
    };
    return categoryMap[cat] || cat;
  };
  const getCategory = (s: ShipmentWithoutBooking) => normalizeCategory(getRawCategory(s));
  const getShipDate = (s: ShipmentWithoutBooking) => s.shipmentDisplay || s.etd || s.shipDate || '';

  // Calculate counts for each filter
  const overdue = shipments.filter(s => getCategory(s) === 'overdue');
  const thirtyDays = shipments.filter(s => getCategory(s) === '30days');
  const sixtyDays = shipments.filter(s => getCategory(s) === '60days');
  const ninetyDays = shipments.filter(s => getCategory(s) === '90days');
  const discretionary = shipments.filter(s => getCategory(s) === 'discretionary');

  const filters: FilterTab[] = [
    { key: 'all', label: 'All', count: shipments.length },
    { key: 'overdue', label: 'Overdue', count: overdue.length },
    { key: '30days', label: '30 Days', count: thirtyDays.length },
    { key: '60days', label: '60 Days', count: sixtyDays.length },
    { key: '90days', label: '90 Days', count: ninetyDays.length },
    { key: 'discretionary', label: 'Discretionary', count: discretionary.length },
  ];

  const filteredShipments = activeFilter === 'all' 
    ? shipments 
    : shipments.filter(s => getCategory(s) === activeFilter);

  const handleReferenceClick = (reference: string) => {
    navigate(`/contracts/${reference}`);
  };

  const filterTabs = (
    <div className="filter-tabs">
      {filters.map((filter) => (
        <button
          key={filter.key}
          className={`filter-tab ${activeFilter === filter.key ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setActiveFilter(filter.key);
          }}
        >
          {filter.label}
          <span className="filter-count">({filter.count})</span>
        </button>
      ))}
    </div>
  );

  return (
    <CollapsibleSection
      title="Shipments Without Bookings"
      count={shipments.length}
      icon={<CalendarIcon />}
      filterTabs={filterTabs}
    >
      {filteredShipments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <div className="empty-state-text">No shipments without bookings</div>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Supplier</th>
                <th>Buyer</th>
                <th>Shipment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredShipments.map((shipment) => {
                const category = getCategory(shipment);
                const shipDate = getShipDate(shipment);
                
                return (
                  <tr 
                    key={shipment.id}
                    className={category === 'overdue' ? 'priority-critical' : ''}
                  >
                    <td>
                      <span 
                        className="ref-link"
                        onClick={() => handleReferenceClick(shipment.reference)}
                      >
                        {shipment.reference}
                      </span>
                    </td>
                    <td>{shipment.supplier}</td>
                    <td>{shipment.buyer}</td>
                    <td>{shipDate}</td>
                    <td>
                      <div className="action-links">
                        <button 
                          className="action-link"
                          onClick={() => onSendBR?.(shipment)}
                        >
                          Send BR
                        </button>
                        <span className="action-separator">|</span>
                        <button 
                          className="action-link primary"
                          onClick={() => onSearchGmail?.(shipment)}
                        >
                          Gmail
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleSection>
  );
};

export default ShipmentsWithoutBookingsSection;
